import type { IBid } from "../services/provider";
import type { VideoRuntime } from "../types/IAdsService";
import type { ResolvedVideoConfig } from "../types/config";

export type VideoAdPhase =
  | "loading"
  | "cta"
  | "starting"
  | "video"
  | "fallback"
  | "finished";

export interface CurrentVideoBid {
  readonly bidder: string;
  readonly cpm: number;
  readonly url: string;
}

export interface VideoAdState {
  readonly phase: VideoAdPhase;
  readonly config: ResolvedVideoConfig;
  readonly runtime: VideoRuntime;
  readonly bids: readonly IBid[] | null;
  readonly currentBid?: CurrentVideoBid;
  readonly generalDuration: number;
  readonly generalTimeLeft: number;
  readonly playerLoaded: boolean;
  readonly videoUrl: string;
  readonly videoVolume: number;
  readonly videoPaused: boolean;
  readonly videoAdCount: number;
  readonly videoRetryCount: number;
  readonly videoIMAFailed: boolean;
}

export type VideoAdEvent =
  | { type: "runtime-ready"; runtime: VideoRuntime }
  | { type: "bids-ready"; bids: readonly IBid[] }
  | { type: "player-ready" }
  | { type: "bid-starting"; remainingBids: readonly IBid[] }
  | { type: "video-ready"; bid: CurrentVideoBid }
  | { type: "retrying"; remainingBids: readonly IBid[] }
  | { type: "fallback"; duration: number }
  | { type: "tick" }
  | { type: "volume-changed"; volume: number }
  | { type: "playback-changed"; paused: boolean }
  | { type: "ima-failed" }
  | { type: "ad-pod-started"; bids: readonly IBid[] }
  | { type: "finished" };

export function createInitialVideoAdState(
  config: ResolvedVideoConfig,
): VideoAdState {
  return {
    phase: "loading",
    config,
    runtime: { player: "ima", fallback: "display" },
    bids: null,
    generalDuration: config.duration,
    generalTimeLeft: config.duration,
    playerLoaded: false,
    videoUrl: "",
    videoVolume: 1,
    videoPaused: false,
    videoAdCount: 1,
    videoRetryCount: 0,
    videoIMAFailed: false,
  };
}

export function videoAdReducer(
  state: VideoAdState,
  event: VideoAdEvent,
): VideoAdState {
  switch (event.type) {
    case "runtime-ready":
      return { ...state, runtime: event.runtime };
    case "bids-ready":
      return { ...state, phase: "cta", bids: [...event.bids] };
    case "player-ready":
      return { ...state, playerLoaded: true };
    case "bid-starting":
      return {
        ...state,
        phase: "starting",
        bids: [...event.remainingBids],
      };
    case "video-ready":
      return {
        ...state,
        phase: "video",
        currentBid: event.bid,
        videoUrl: event.bid.url,
        videoPaused: false,
      };
    case "retrying":
      return {
        ...state,
        phase: "starting",
        bids: [...event.remainingBids],
        videoRetryCount: state.videoRetryCount + 1,
      };
    case "fallback":
      return {
        ...state,
        phase: "fallback",
        currentBid: undefined,
        generalDuration: event.duration,
        generalTimeLeft: event.duration,
      };
    case "tick":
      if (state.videoPaused || state.generalTimeLeft <= 0) return state;
      return { ...state, generalTimeLeft: state.generalTimeLeft - 1 };
    case "volume-changed":
      return { ...state, videoVolume: event.volume };
    case "playback-changed":
      return { ...state, videoPaused: event.paused };
    case "ima-failed":
      return { ...state, videoIMAFailed: true };
    case "ad-pod-started":
      return {
        ...state,
        bids: [...event.bids],
        videoAdCount: state.videoAdCount + 1,
      };
    case "finished":
      return { ...state, phase: "finished", generalTimeLeft: 0 };
  }
}
